import { t } from '@/lib/i18n';
import { Users as UsersIcon, ShieldCheck, Shield, CircleCheck, CircleSlash } from 'lucide-react';
import { CenteredSpinner, ErrorState } from '@/components/ui';
import { cn, formatDate } from '@/lib/format';
import { useMe, useSetUserActive, useSetUserAdmin, useUsers } from '@/lib/queries';

/** Admin-only user management: list, activate/deactivate, grant/revoke admin. */
export function UsersPage() {
  const { data: users, isPending, error, refetch } = useUsers();
  const { data: me } = useMe();
  const setActive = useSetUserActive();
  const setAdmin = useSetUserAdmin();

  if (isPending) return <CenteredSpinner label={t('usersPage.loadingUsers')} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!users) return null;

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="w-full px-6 py-6">
        <div className="mb-4 flex items-center gap-2">
          <UsersIcon className="size-5 text-brand-500" />
          <h1 className="text-lg font-semibold text-strong">{t('usersPage.users')}</h1>
          <span className="text-sm text-muted">· {users.length}</span>
        </div>

        <div className="overflow-hidden rounded-xl border line surface-card">
          <table className="w-full text-sm">
            <thead className="surface-sunken text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t('usersPage.user')}</th>
                <th className="px-4 py-2 font-medium">{t('usersPage.email')}</th>
                <th className="px-4 py-2 font-medium">{t('usersPage.lastLogin')}</th>
                <th className="px-4 py-2 text-center font-medium">{t('usersPage.admin')}</th>
                <th className="px-4 py-2 text-center font-medium">{t('usersPage.active')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const self = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-t line">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-strong">{u.name}</div>
                      <div className="text-xs text-muted">@{u.username}{self && t('usersPage.you')}</div>
                    </td>
                    <td className="px-4 py-2.5 text-body">{u.email ?? <span className="text-faint">—</span>}</td>
                    <td className="px-4 py-2.5 text-muted">{u.lastLoginAt ? formatDate(u.lastLoginAt) : <span className="text-faint">{t('usersPage.never')}</span>}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Toggle
                        on={u.isAdmin}
                        disabled={self || setAdmin.isPending}
                        onIcon={<ShieldCheck className="size-4" />}
                        offIcon={<Shield className="size-4" />}
                        title={self ? t('usersPage.cantChangeOwnAdmin') : u.isAdmin ? t('usersPage.revokeAdmin') : t('usersPage.grantAdmin')}
                        onClick={() => setAdmin.mutate({ id: u.id, isAdmin: !u.isAdmin })}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Toggle
                        on={u.isActive}
                        disabled={self || setActive.isPending}
                        onIcon={<CircleCheck className="size-4" />}
                        offIcon={<CircleSlash className="size-4" />}
                        title={self ? t('usersPage.cantDeactivateSelf') : u.isActive ? t('usersPage.deactivate') : t('usersPage.activate')}
                        onClick={() => setActive.mutate({ id: u.id, isActive: !u.isActive })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted">
          {t('usersPage.footerStart')} (<code className="text-faint">scripts/set-user.mjs</code>) {t('usersPage.footerEnd')}
        </p>
      </div>
    </div>
  );
}

function Toggle({ on, onIcon, offIcon, onClick, disabled, title }: {
  on: boolean; onIcon: React.ReactNode; offIcon: React.ReactNode; onClick: () => void; disabled?: boolean; title: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-grid size-7 place-items-center rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
        on ? 'bg-brand-500/15 text-brand-600' : 'surface-sunken text-faint hover:text-muted',
      )}
    >
      {on ? onIcon : offIcon}
    </button>
  );
}
